import { useEffect, useMemo, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Link } from 'wouter';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { ExternalLink, Loader2, MapPin, Pencil, RotateCcw, Save, Trash2, Upload, X } from 'lucide-react';
import { apiRequest } from '@/lib/queryClient';
import { useToast } from '@/hooks/use-toast';
import type { AisleDefinition, FloorplanZone, FloorplanZoneOutlineShape } from '../types';
import {
  buildInstoreLocationFloorplanRows,
  createZoneFromInstoreLocation,
  sanitizeFloorplanWritePayload,
  unlinkedFloorplanZones,
} from '../lib/floorplanZoneUtils';
import {
  buildZoneOutlineColors,
  zoneOutlineColor,
} from '../lib/instoreLocationGroups';
import { cacheBustUrl } from '../lib/cacheBustUrl';
import { FloorplanCanvas } from './FloorplanCanvas';

interface FloorplanEditorProps {
  storeId: number;
  storeIdParam: string;
  retailerId?: number;
  aisles: AisleDefinition[];
  zones: FloorplanZone[];
  floorplanImageUrl?: string | null;
  floorplanImageVersion?: string | null;
  retailerInstoreLocations?: Array<{ id: number; name: string }>;
  /** When set, only these store locations are used for floorplan mapping (subset of retailer catalog) */
  storeInstoreLocations?: Array<{ id: number; name: string }>;
  /** Locations currently referenced by campaign posting instructions. */
  campaignInstoreLocationIds?: number[];
  onEditingChange?: (isEditing: boolean) => void;
}

type DraftAisle = Omit<AisleDefinition, 'dataSource'>;

const OUTLINE_SHAPES: FloorplanZoneOutlineShape[] = ['rectangle', 'rounded', 'ellipse'];

function toDraftAisles(aisles: AisleDefinition[]): DraftAisle[] {
  return aisles.map(({ dataSource: _dataSource, ...aisle }) => aisle);
}

async function invalidateFloorplanQueries(
  queryClient: ReturnType<typeof useQueryClient>,
  storeIdParam: string,
) {
  await Promise.all([
    queryClient.invalidateQueries({ queryKey: ['/api/store-profiles', storeIdParam, 'context'] }),
    queryClient.invalidateQueries({ queryKey: ['/api/stores', storeIdParam, 'detail'] }),
  ]);
}

export function FloorplanEditor({
  storeId,
  storeIdParam,
  retailerId,
  aisles,
  zones,
  floorplanImageUrl,
  floorplanImageVersion,
  retailerInstoreLocations = [],
  storeInstoreLocations,
  campaignInstoreLocationIds = [],
  onEditingChange,
}: FloorplanEditorProps) {
  const [isEditing, setIsEditing] = useState(false);

  const setEditing = (next: boolean) => {
    setIsEditing(next);
    onEditingChange?.(next);
  };
  const [draftAisles, setDraftAisles] = useState<DraftAisle[]>(() => toDraftAisles(aisles));
  const [draftZones, setDraftZones] = useState<FloorplanZone[]>(zones);
  const [previewImageUrl, setPreviewImageUrl] = useState<string | null>(
    cacheBustUrl(floorplanImageUrl, floorplanImageVersion),
  );
  const [previewImageVersion, setPreviewImageVersion] = useState<string | null>(floorplanImageVersion ?? null);
  const [activeLocationId, setActiveLocationId] = useState<number | null>(null);
  const [selectedZoneId, setSelectedZoneId] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isResetting, setIsResetting] = useState(false);
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const [pendingStoreLocationIds, setPendingStoreLocationIds] = useState<number[]>([]);
  const [isSavingStoreLocations, setIsSavingStoreLocations] = useState(false);
  const [isSelectingStoreLocations, setIsSelectingStoreLocations] = useState(false);
  const imageInputRef = useRef<HTMLInputElement>(null);
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const locationsForStore = useMemo(() => {
    if (storeInstoreLocations != null) return storeInstoreLocations;
    return retailerInstoreLocations;
  }, [storeInstoreLocations, retailerInstoreLocations]);

  const sortedRetailerLocations = useMemo(
    () => [...retailerInstoreLocations].sort((a, b) => a.name.localeCompare(b.name)),
    [retailerInstoreLocations],
  );
  const campaignLocationIdSet = useMemo(
    () => new Set(campaignInstoreLocationIds),
    [campaignInstoreLocationIds],
  );

  useEffect(() => {
    if (!isSelectingStoreLocations) {
      setPendingStoreLocationIds(
        locationsForStore.map((location) => location.id),
      );
    }
  }, [isSelectingStoreLocations, locationsForStore, storeId]);

  const locationRows = useMemo(
    () => buildInstoreLocationFloorplanRows(locationsForStore, draftZones),
    [locationsForStore, draftZones],
  );

  const mappedCount = locationRows.filter((row) => row.isMapped).length;
  const unlinkedZones = useMemo(() => unlinkedFloorplanZones(draftZones), [draftZones]);
  const draftZoneOutlineColors = useMemo(
    () => buildZoneOutlineColors(draftZones),
    [draftZones],
  );

  const selectedZone = draftZones.find((zone) => zone.id === selectedZoneId) ?? null;

  useEffect(() => {
    setPreviewImageUrl(cacheBustUrl(floorplanImageUrl, floorplanImageVersion));
    setPreviewImageVersion(floorplanImageVersion ?? null);
  }, [floorplanImageUrl, floorplanImageVersion]);

  useEffect(() => {
    if (!isEditing) {
      setDraftAisles(toDraftAisles(aisles));
      setDraftZones(zones);
      setActiveLocationId(null);
      setSelectedZoneId(null);
    }
  }, [aisles, zones, isEditing]);

  const updateZone = (id: string, patch: Partial<FloorplanZone>) => {
    setDraftZones((current) => current.map((zone) => (zone.id === id ? { ...zone, ...patch } : zone)));
  };

  const removeZone = (id: string) => {
    setDraftZones((current) => current.filter((zone) => zone.id !== id));
    if (selectedZoneId === id) {
      setSelectedZoneId(null);
    }
  };

  const selectLocation = (locationId: number) => {
    setActiveLocationId(locationId);
    const row = locationRows.find((item) => item.location.id === locationId);
    // An unmapped location is a new drawing target. Do not leave the previous
    // zone selected or the editor appears to create another copy of that area.
    setSelectedZoneId(row?.zone?.id ?? null);
  };

  const handleZoneCreate = (bounds: Pick<FloorplanZone, 'x' | 'y' | 'width' | 'height'>) => {
    if (activeLocationId == null) {
      toast({
        title: 'Select an in-store location first',
        description: 'Pick a location from the list, then draw its shape on the floorplan.',
      });
      return;
    }

    const location = locationsForStore.find((item) => item.id === activeLocationId);
    if (!location) return;

    const existing = draftZones.find((zone) => zone.instoreLocationId === activeLocationId);
    if (existing) {
      updateZone(existing.id, bounds);
      setSelectedZoneId(existing.id);
      return;
    }

    const zone = createZoneFromInstoreLocation(location, draftZones.length, draftZones);
    setDraftZones((current) => [
      ...current,
      {
        ...zone,
        outlineColor: zoneOutlineColor(current.length),
        ...bounds,
      },
    ]);
    setSelectedZoneId(zone.id);
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const payload = sanitizeFloorplanWritePayload({
        aisles: draftAisles as unknown as Array<Record<string, unknown>>,
        zones: draftZones.map((zone) => ({
          ...zone,
          outlineColor:
            zone.outlineColor ?? draftZoneOutlineColors[zone.id],
        })) as unknown as Array<Record<string, unknown>>,
      });
      const res = await apiRequest(`/api/store-profiles/${storeId}/floorplan`, 'PUT', payload);
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        const detail = Array.isArray(body.details)
          ? body.details.map((item: { message?: string }) => item.message).filter(Boolean).join('; ')
          : null;
        throw new Error(detail || body.error || 'Failed to save floorplan');
      }
      await invalidateFloorplanQueries(queryClient, storeIdParam);
      toast({ title: 'Floorplan saved' });
      setEditing(false);
    } catch (error) {
      toast({
        title: 'Could not save floorplan',
        description: error instanceof Error ? error.message : 'Unknown error',
        variant: 'destructive',
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleUploadImage = async (file: File) => {
    setIsUploadingImage(true);
    try {
      const formData = new FormData();
      formData.append('image', file);
      const res = await apiRequest(`/api/store-profiles/${storeId}/floorplan/upload-image`, 'POST', formData);
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(body.message ?? body.error ?? 'Failed to upload floorplan');
      }

      const nextVersion = body.updatedAt ?? String(Date.now());
      setPreviewImageUrl(cacheBustUrl(body.imageUrl ?? null, nextVersion));
      setPreviewImageVersion(nextVersion);
      setDraftAisles([]);
      setDraftZones([]);
      setActiveLocationId(null);
      setSelectedZoneId(null);
      await invalidateFloorplanQueries(queryClient, storeIdParam);
      toast({
        title: 'Floorplan image replaced',
        description: 'Previous shapes were cleared. Select each in-store location and draw its area on the new plan.',
      });
    } catch (error) {
      toast({
        title: 'Upload failed',
        description: error instanceof Error ? error.message : 'Unknown error',
        variant: 'destructive',
      });
    } finally {
      setIsUploadingImage(false);
    }
  };

  const handleSaveStoreLocations = async () => {
    setIsSavingStoreLocations(true);
    try {
      const response = await apiRequest(`/api/store-profiles/${storeId}/overview`, 'PATCH', {
        instoreLocationIds: [...pendingStoreLocationIds].sort((a, b) => a - b),
      });
      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.error ?? 'Failed to save store locations');
      }
      await invalidateFloorplanQueries(queryClient, storeIdParam);
      setIsSelectingStoreLocations(false);
      toast({ title: 'Store locations updated' });
    } catch (error) {
      toast({
        title: 'Could not save locations',
        description: error instanceof Error ? error.message : 'Update failed',
        variant: 'destructive',
      });
    } finally {
      setIsSavingStoreLocations(false);
    }
  };

  const handleReset = async () => {
    setIsResetting(true);
    try {
      const res = await apiRequest(`/api/store-profiles/${storeId}/floorplan`, 'DELETE');
      if (!res.ok && res.status !== 404) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error ?? 'Failed to reset floorplan');
      }
      setPreviewImageUrl(null);
      setPreviewImageVersion(null);
      setActiveLocationId(null);
      setSelectedZoneId(null);
      await invalidateFloorplanQueries(queryClient, storeIdParam);
      toast({ title: 'Saved floorplan removed', description: 'Using demo layout again until you save a new one.' });
      setEditing(false);
    } catch (error) {
      toast({
        title: 'Could not reset floorplan',
        description: error instanceof Error ? error.message : 'Unknown error',
        variant: 'destructive',
      });
    } finally {
      setIsResetting(false);
    }
  };

  if (!isEditing) {
    return (
      <div className="flex flex-wrap items-center gap-2">
        <Button type="button" variant="outline" size="sm" onClick={() => setEditing(true)}>
          <Pencil className="h-4 w-4 mr-2" />
          Edit floorplan
        </Button>
        <p className="text-xs text-gray-500">
          Upload a plan image, then associate each in-store location with a shape on the floorplan.
        </p>
      </div>
    );
  }

  const activeLocation = locationsForStore.find((item) => item.id === activeLocationId);

  return (
    <Card className="border-blue-200 bg-blue-50/40">
      <CardHeader className="pb-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <CardTitle className="text-base">Edit floorplan</CardTitle>
          <div className="flex flex-wrap gap-2">
            <Button type="button" variant="outline" size="sm" onClick={() => setEditing(false)} disabled={isSaving}>
              <X className="h-4 w-4 mr-1" />
              Cancel
            </Button>
            <Button type="button" variant="outline" size="sm" onClick={handleReset} disabled={isSaving || isResetting}>
              {isResetting ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <RotateCcw className="h-4 w-4 mr-1" />}
              Reset saved
            </Button>
            <Button type="button" size="sm" onClick={handleSave} disabled={isSaving}>
              {isSaving ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Save className="h-4 w-4 mr-1" />}
              Save
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="space-y-2">
          <Label>Floorplan image</Label>
          <div className="flex flex-wrap items-center gap-2">
            <input
              ref={imageInputRef}
              type="file"
              accept="image/png,image/jpeg,image/jpg,image/webp,application/pdf"
              className="hidden"
              onChange={(event) => {
                const file = event.target.files?.[0];
                event.target.value = '';
                if (file) void handleUploadImage(file);
              }}
            />
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={isUploadingImage}
              onClick={() => imageInputRef.current?.click()}
            >
              {isUploadingImage ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Upload className="h-4 w-4 mr-1" />}
              {previewImageUrl ? 'Replace plan image' : 'Upload plan image'}
            </Button>
          </div>
        </div>

        {(isSelectingStoreLocations || locationsForStore.length === 0)
          && sortedRetailerLocations.length > 0 && (
          <div className="space-y-3 rounded-lg border border-blue-200 bg-white px-3 py-3">
            <div>
              <p className="text-sm font-medium text-slate-950">
                Applicable locations for this store
              </p>
              <p className="text-xs text-slate-600 mt-0.5">
                Campaign locations are included automatically. Add any other
                retailer locations that physically exist in this store.
              </p>
            </div>
            <div className="max-h-64 overflow-y-auto rounded-lg border p-2 space-y-1 bg-white">
              {sortedRetailerLocations.map((location) => {
                const checked = pendingStoreLocationIds.includes(location.id);
                const usedByCampaign = campaignLocationIdSet.has(location.id);
                const mapped = draftZones.some(
                  (zone) => zone.instoreLocationId === location.id,
                );
                const required = usedByCampaign || mapped;
                return (
                  <label
                    key={location.id}
                    className="flex items-center gap-2 rounded-md px-2 py-1.5 hover:bg-slate-50 cursor-pointer text-sm"
                  >
                    <Checkbox
                      checked={checked || required}
                      disabled={required}
                      onCheckedChange={(value) => {
                        const on = value === true;
                        setPendingStoreLocationIds((current) => {
                          if (on) {
                            return current.includes(location.id)
                              ? current
                              : [...current, location.id];
                          }
                          return current.filter((id) => id !== location.id);
                        });
                      }}
                      aria-label={location.name}
                    />
                    <span className="flex-1 truncate">{location.name}</span>
                    {usedByCampaign ? (
                      <Badge variant="secondary" className="text-[10px]">
                        From campaigns
                      </Badge>
                    ) : mapped ? (
                      <Badge variant="outline" className="text-[10px]">
                        On plan
                      </Badge>
                    ) : null}
                  </label>
                );
              })}
            </div>
            <div className="flex flex-wrap gap-2">
              <Button
                type="button"
                size="sm"
                disabled={isSavingStoreLocations}
                onClick={() => void handleSaveStoreLocations()}
              >
                {isSavingStoreLocations ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  'Save applicable locations'
                )}
              </Button>
              {locationsForStore.length > 0 && (
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  disabled={isSavingStoreLocations}
                  onClick={() => setIsSelectingStoreLocations(false)}
                >
                  Cancel
                </Button>
              )}
            </div>
          </div>
        )}

        {locationsForStore.length > 0 && !isSelectingStoreLocations && (
          <div className="space-y-2">
            <div className="flex flex-wrap items-start justify-between gap-2">
              <div>
                <p className="text-sm font-medium">Location layers</p>
                <p className="text-xs text-gray-600 mt-0.5">
                  Select a location, then draw its area on the floorplan.{' '}
                  {mappedCount} of {locationRows.length} mapped.
                </p>
              </div>
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={() => setIsSelectingStoreLocations(true)}
              >
                Add or remove locations
              </Button>
            </div>
            <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 max-h-56 overflow-y-auto border rounded-lg p-2 bg-white">
              {locationRows.map(({ location, isMapped, zone }) => {
                const isActive = activeLocationId === location.id;
                return (
                  <li key={location.id}>
                    <button
                      type="button"
                      onClick={() => selectLocation(location.id)}
                      className={`w-full flex items-center gap-2 rounded-md border px-2.5 py-2 text-left text-sm transition-colors ${
                        isActive ? 'border-blue-500 bg-blue-50 ring-1 ring-blue-200' : 'hover:bg-gray-50'
                      }`}
                      aria-pressed={isActive}
                    >
                      <MapPin className={`h-3.5 w-3.5 shrink-0 ${isMapped ? 'text-green-600' : 'text-amber-500'}`} aria-hidden />
                      <span className="flex-1 truncate font-medium">{location.name}</span>
                      {campaignLocationIdSet.has(location.id) && (
                        <span
                          className="h-2 w-2 rounded-full bg-blue-500"
                          title="Used by campaign posting instructions"
                          aria-label="Used by campaigns"
                        />
                      )}
                      <Badge variant={isMapped ? 'secondary' : 'outline'} className="text-[10px] shrink-0">
                        {isMapped ? 'On plan' : 'Not mapped'}
                      </Badge>
                    </button>
                    {isActive && zone && (
                      <p className="text-[11px] text-gray-500 mt-1 px-1">Shape selected — drag to move or use corners to resize.</p>
                    )}
                    {isActive && !zone && previewImageUrl && (
                      <p className="text-[11px] text-blue-700 mt-1 px-1">Draw a box on the plan below for this location.</p>
                    )}
                  </li>
                );
              })}
            </ul>
          </div>
        )}

        {sortedRetailerLocations.length === 0 && (
          <p className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
            Couldn&apos;t load in-store locations for this retailer. Refresh the page — they should come from the retailer record, not be re-entered here.
          </p>
        )}

        {previewImageUrl && (
          <div className="space-y-2">
            <Label>
              Floorplan
              {activeLocation ? ` — placing ${activeLocation.name}` : ' — select a location above to draw'}
            </Label>
            <FloorplanCanvas
              zones={draftZones}
              imageUrl={cacheBustUrl(previewImageUrl, previewImageVersion)}
              selectedZoneId={selectedZoneId}
              zoneOutlineColors={draftZoneOutlineColors}
              editable
              onZoneClick={(zoneId) => {
                setSelectedZoneId(zoneId);
                const zone = draftZones.find((item) => item.id === zoneId);
                if (zone?.instoreLocationId != null) {
                  setActiveLocationId(zone.instoreLocationId);
                }
              }}
              onZoneMove={(zoneId, patch) => updateZone(zoneId, patch)}
              onZoneResize={(zoneId, patch) => updateZone(zoneId, patch)}
              onZoneCreate={activeLocationId != null ? handleZoneCreate : undefined}
              drawHint={
                activeLocationId != null
                  ? 'Click and drag on the plan to draw this location’s shape. Drag existing shapes to reposition.'
                  : 'Select an in-store location from the list above, then draw its shape here.'
              }
            />
          </div>
        )}

        {selectedZone && (
          <div className="border rounded-lg p-3 bg-white space-y-3">
            <div className="flex items-start justify-between gap-2">
              <div>
                <p className="text-sm font-medium">{selectedZone.label}</p>
                <p className="text-xs text-gray-500 mt-0.5">
                  Drag the area to move it, use its corner handles to resize it,
                  or change its appearance below.
                </p>
              </div>
              <div className="flex flex-wrap justify-end gap-2">
                {retailerId != null && selectedZone.instoreLocationId != null && (
                  <Button type="button" variant="outline" size="sm" asChild>
                    <Link href={`/admin/retailers/${retailerId}/in-store-locations?locationId=${selectedZone.instoreLocationId}`}>
                      <ExternalLink className="h-3.5 w-3.5 mr-1" />
                      Edit retailer location
                    </Link>
                  </Button>
                )}
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="text-red-700 hover:text-red-800"
                  onClick={() => removeZone(selectedZone.id)}
                >
                  <Trash2 className="h-3.5 w-3.5 mr-1" />
                  Remove area
                </Button>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <Label className="text-xs">Outline shape</Label>
                <Select
                  value={selectedZone.outlineShape ?? 'rounded'}
                  onValueChange={(value: FloorplanZoneOutlineShape) => updateZone(selectedZone.id, { outlineShape: value })}
                >
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {OUTLINE_SHAPES.map((shape) => (
                      <SelectItem key={shape} value={shape}>{shape}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label
                  className="text-xs"
                  htmlFor={`zone-colour-${selectedZone.id}`}
                >
                  Layer colour
                </Label>
                <div className="flex h-10 items-center gap-2 rounded-md border bg-white px-2">
                  <input
                    id={`zone-colour-${selectedZone.id}`}
                    type="color"
                    aria-label="Layer colour"
                    className="h-7 w-10 cursor-pointer rounded border-0 bg-transparent p-0"
                    value={
                      selectedZone.outlineColor
                      ?? draftZoneOutlineColors[selectedZone.id]
                      ?? '#0284c7'
                    }
                    onChange={(event) =>
                      updateZone(selectedZone.id, {
                        outlineColor: event.target.value,
                      })
                    }
                  />
                  <span className="font-mono text-xs uppercase text-slate-500">
                    {selectedZone.outlineColor
                      ?? draftZoneOutlineColors[selectedZone.id]
                      ?? '#0284c7'}
                  </span>
                </div>
              </div>
              <div>
                <Label className="text-xs">Line weight</Label>
                <Select
                  value={String(selectedZone.outlineStrokeWidth ?? 0.2)}
                  onValueChange={(value) =>
                    updateZone(selectedZone.id, {
                      outlineStrokeWidth: Number(value),
                    })
                  }
                >
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="0.12">Fine</SelectItem>
                    <SelectItem value="0.2">Standard</SelectItem>
                    <SelectItem value="0.35">Bold</SelectItem>
                    <SelectItem value="0.5">Highlight</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
        )}

        {unlinkedZones.length > 0 && (
          <p className="text-xs text-amber-700">
            {unlinkedZones.length} shape{unlinkedZones.length === 1 ? '' : 's'} on the plan {unlinkedZones.length === 1 ? 'is' : 'are'} not linked to a retailer location — select each shape and remove it, or re-draw from the location list.
          </p>
        )}
      </CardContent>
    </Card>
  );
}
